/* eslint-disable sonarjs/cognitive-complexity */
import moment, { Moment } from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { AnchorDirectionShape, DateRangePicker, FocusedInputShape, isSameDay } from 'react-dates';
import 'react-dates/initialize';
import Button from './Button';
import DatePickerWrapper from './DatePickerWrapper';
import Header from './Header';
import InfoPanel from './InfoPanel';
import { DateHighlightProps, ICalendar } from './types';

import { mapToCssModules } from '../../../utils';

export type onChangeType = { startDate: Moment | null; endDate: Moment | null };

interface ICalendarType extends ICalendar {
  isOpenDatePicker?: boolean;
  showCloseButton?: boolean;
  setIsOpenDatePicker?: (value: boolean) => void;
  onSubmit: (skipped?: boolean) => void;
  onPrevious?: () => void;
  onToggle?: () => void;
  onSkip?: () => void;
  onClear?: () => void;
  onMobileClose?: () => void;
  hasNavHeader?: boolean;
}

const SmlsCalendar = ({
  id,
  idConfirmButton = 'btn_confirmCalendar',
  idNextIconButton = 'btn_nextCalendar',
  idPrevIconButton = 'btn_prevCalendar',
  idErrorMessage = 'lbl_msgInvalid_',
  disabled = false,
  anchorDirection = 'right',
  onChange = () => undefined,
  onSubmit = () => undefined,
  onPrevious = () => undefined,
  onSkip = () => undefined,
  onClear = () => undefined,
  onToggle = () => undefined,
  hasNavHeader = false,
  startDateInitialValue,
  endDateInitialValue,
  startDateValue,
  endDateValue,
  displayFormat = 'ddd, DD MMM',
  initialDatesPattern = 'YYYY-MM-DD',
  startDateId,
  endDateId,
  startDatePlaceholderText = 'Check In',
  endDatePlaceholderText = 'Check Out',
  mobilePlaceholderText,
  datesListHighlight = [],
  numberOfMonths = 2,
  errorMessage,
  isSingleDate = false,
  singleDateTitleHeader = 'Data da viagem',
  minimumNights = 1,
  forceError = false,
  forceFocus,
  minDate = 1,
  maxDate = 24,
  isOutsideRange = (day) => day.isBefore(moment().add(1, 'days')),
  renderDayContents,
  renderMonthElement,
  isOpenDatePicker = false,
  showCloseButton = true,
  setIsOpenDatePicker,
  onMobileClose = () => undefined,
}: ICalendarType) => {
  const inputRef = useRef<HTMLDivElement>(null);

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(null);
  const [selectedMoment, setSelectedMoment] = useState<Moment>(moment().startOf('month'));
  const [isMobileOpenSelector, setIsMobileOpenSelector] = useState<boolean>(isOpenDatePicker);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isValidSelectEndDate, setTooltipEnable] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<Moment | null>(
    startDateInitialValue ? moment(startDateInitialValue, initialDatesPattern) : null,
  );
  const [endDate, setEndDate] = useState<Moment | null>(
    endDateInitialValue ? moment(endDateInitialValue, initialDatesPattern) : null,
  );

  const [deviceWidth, setDeviceWidth] = useState(window.innerWidth);

  const onDatesChange = (onChangeProps: onChangeType) => {
    let output = { startDate: onChangeProps.startDate, endDate: onChangeProps.endDate };
    setStartDate(onChangeProps.startDate);
    setEndDate(onChangeProps.endDate);

    if (moment(startDate).isAfter(onChangeProps.startDate)) {
      setEndDate(null);
      output = { startDate: onChangeProps.startDate, endDate: null };
    }

    setIsValid((onChangeProps.startDate?.isValid() && onChangeProps.endDate?.isValid()) || false);

    if (onChange) {
      onChange(output);
    }
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setFocusedInput('startDate');
    onClear();
  };

  const handlePrevious = () => {
    onPrevious();
    setIsMobileOpenSelector(false);

    if (setIsOpenDatePicker) {
      setIsOpenDatePicker(false);
    }
  };

  const handleClose = () => {
    onMobileClose();
    setFocusedInput(null);

    setIsMobileOpenSelector(false);

    if (setIsOpenDatePicker) {
      setIsOpenDatePicker(false);
    }
  };

  /* const handleSkip = () => {
    onSkip();
    setIsMobileOpenSelector(false);
  }; */

  const handleSubmit = () => {
    if (onChange) {
      onChange({ startDate, endDate });
    }

    setFocusedInput(null);
    onSubmit();
  };

  const handleOnFocusChange = (inputChange: FocusedInputShape | null) => {
    if (isSingleDate && inputChange === 'endDate') {
      return;
    }

    if (!inputChange) {
      setFocusedInput(null);
      return;
    }

    setFocusedInput(inputChange);
  };

  const handleToggleIsModalSelector = (isOpen: boolean) => {
    onToggle();
    setIsMobileOpenSelector(isOpen);

    if (setIsOpenDatePicker) {
      setIsOpenDatePicker(isOpen);
    }
  };

  const onDateHover = (date: Moment) => {
    const dateIsSame = startDate?.isSame(date);
    setTooltipEnable(!dateIsSame);
  };

  useEffect(() => {
    if (startDateInitialValue) {
      setStartDate(moment(startDateInitialValue));
    }

    if (endDateInitialValue) {
      setEndDate(moment(endDateInitialValue));
    }
  }, [startDateInitialValue, endDateInitialValue]);

  useEffect(() => {
    moment.locale('pt-br');
  }, []);

  useEffect(() => {
    if (isOpenDatePicker) {
      setIsMobileOpenSelector(isOpenDatePicker);
    }
  }, [isOpenDatePicker]);

  useEffect(() => {
    if (forceFocus) {
      setFocusedInput('startDate');
    }
  }, [forceFocus]);

  useEffect(() => {
    const handleResize = () => setDeviceWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (deviceWidth <= 768) {
    return (
      <div
        id={id}
        ref={inputRef}
        className={mapToCssModules([
          'smls-calendar smls-calendar-mobile',
          disabled ? 'disabled' : '',
          isMobileOpenSelector ? 'open' : 'close',
        ])}
      >
        {hasNavHeader && (
          <div className={`calendar-nav-header ${isMobileOpenSelector ? 'active' : ''}`}>
            <button type="button" onClick={handlePrevious}>
              <i className="material-icons">chevron_left</i>
            </button>
            {startDate || endDate ? (
              <button type="button" onClick={handleClearDates}>
                Limpar
              </button>
            ) : /* == Não necessário na V1 ==
              <button type="button" onClick={handleSkip}>
                Pular
              </button> */
            null}
          </div>
        )}
        <DatePickerWrapper
          onToggle={handleToggleIsModalSelector}
          isDayBlocked={(day) => (startDate ? day.isAfter(moment(startDate).add(98, 'days')) : false)}
          startDate={startDateValue ? moment(startDateValue, initialDatesPattern) : startDate}
          disabled={disabled}
          endDate={endDateValue ? moment(endDateValue, initialDatesPattern) : endDate}
          displayFormat={displayFormat}
          onDatesChange={onDatesChange}
          isSingleDate={isSingleDate}
          singleDateTitleHeader={singleDateTitleHeader}
          isOpen={isMobileOpenSelector}
          forceFocus={forceFocus}
          isOutsideRange={isOutsideRange}
          datesListHighlight={datesListHighlight}
          startDatePlaceholderText={
            mobilePlaceholderText && !isMobileOpenSelector ? mobilePlaceholderText : startDatePlaceholderText
          }
          endDatePlaceholderText={isMobileOpenSelector ? endDatePlaceholderText : ''}
          renderDayContents={renderDayContents}
          renderMonthElement={renderMonthElement}
          onSubmit={handleSubmit}
          onClose={handleClose}
          showCloseButton={showCloseButton}
        />
        <div className={mapToCssModules(['smls-input-data-backdrop', isMobileOpenSelector ? 'open' : ''])} />
      </div>
    );
  }

  return (
    <div
      id={id}
      className={mapToCssModules([
        'smls-calendar',
        isSingleDate ? 'single-input' : '',
        disabled ? 'disabled' : '',
        (!isValid && !focusedInput && !isSingleDate) || forceError ? 'error-calendar-input' : '',
      ])}
    >
      <DateRangePicker
        isOutsideRange={isOutsideRange}
        isDayBlocked={(day) => (startDate ? day.isAfter(moment(startDate).add(98, 'days')) : false)}
        startDateId={startDateId}
        endDateId={endDateId}
        onDatesChange={onDatesChange}
        onFocusChange={handleOnFocusChange}
        focusedInput={focusedInput}
        minimumNights={isSingleDate ? 0 : minimumNights}
        startDate={startDateValue ? moment(startDateValue, initialDatesPattern) : startDate}
        endDate={endDateValue ? moment(endDateValue, initialDatesPattern) : endDate}
        // navPrev={<PrevIcon idPrevIconButton={idPrevIconButton} />}
        // navNext={<NextIcon idNextIconButton={idNextIconButton} />}
        showDefaultInputIcon
        readOnly
        disabled={disabled}
        keepOpenOnDateSelect
        inputIconPosition="after"
        calendarInfoPosition="bottom"
        customArrowIcon={<Button type="" />}
        customInputIcon={<Button type="calendar" />}
        renderDayContents={(day) => {
          if (renderDayContents) {
            return renderDayContents;
          }

          if (!isValidSelectEndDate && focusedInput === 'endDate') {
            return (
              <span
                id="lbl_notificationsCalendar"
                data-title="Entrada e saída devem ser em dias diferentes"
                onMouseEnter={() => onDateHover(day)}
              >
                {moment(day).format('D')}
              </span>
            );
          }

          return (
            <span
              id={`date_${moment(day).format('DDMY')}`}
              onClick={(e) => {
                if (focusedInput === 'startDate') {
                  e.currentTarget.id = 'lbl_selectedDate_1';
                }

                if (focusedInput === 'endDate') {
                  e.currentTarget.id = 'lbl_selectedDate_2';
                }
              }}
              onMouseEnter={() => onDateHover(day)}
            >
              {moment(day).format('D')}
            </span>
          );
        }}
        verticalHeight={1000}
        numberOfMonths={numberOfMonths}
        renderCalendarInfo={() => (
          <InfoPanel
            idConfirmButton={idConfirmButton}
            onClick={handleSubmit}
            onClear={handleClearDates}
            datesListHighlight={datesListHighlight}
            hasSelectedDate={startDate || endDate}
            selectedDate={selectedMoment}
            startDate={startDate}
            endDate={endDate}
            disabledButton={isSingleDate && (startDate || startDateValue) ? !isSingleDate : !(startDate && endDate)}
          />
        )}
        onClose={() => {
          // setStartDate(null);
          // setEndDate(null);
          onPrevious();
        }}
        anchorDirection={anchorDirection as AnchorDirectionShape}
        transitionDuration={200}
        minDate={moment().subtract(minDate, 'months').startOf('month')}
        maxDate={moment().add(maxDate, 'months').endOf('month')}
        renderMonthElement={(momentMonth) => {
          if (renderMonthElement) {
            return renderMonthElement(momentMonth);
          }

          return (
            <Header
              leftIconId={id}
              rightIconId={id}
              currentMoment={momentMonth.month}
              selectedMoment={selectedMoment}
              minDate={minDate}
              maxDate={maxDate}
            />
          );
        }}
        startDatePlaceholderText={startDatePlaceholderText}
        endDatePlaceholderText={endDatePlaceholderText}
        onNextMonthClick={(current) => {
          if (
            current.get('month') === moment().subtract(minDate, 'months').startOf('month').get('month') &&
            current.get('year') === moment().subtract(minDate, 'months').startOf('month').get('year')
          ) {
            return;
          }

          setSelectedMoment(current);
          const picker = document.getElementById(id);

          if (!picker) {
            return;
          }

          const buttonLeft = picker?.getElementsByClassName('calendar-navigation button-left');
          if (buttonLeft && buttonLeft.length && buttonLeft.length > 0) {
            buttonLeft[0].removeAttribute('hidden');
          }
        }}
        onPrevMonthClick={(current) => {
          if (
            current.get('month') === moment().add(maxDate, 'months').endOf('month').get('month') &&
            current.get('year') === moment().add(maxDate, 'months').endOf('month').get('year')
          ) {
            return;
          }

          setSelectedMoment(current);
          const picker = document.getElementById(id);

          if (!picker) {
            return;
          }

          const buttonRight = picker?.getElementsByClassName('calendar-navigation button-right');
          if (buttonRight && buttonRight.length && buttonRight.length > 0) {
            buttonRight[0].removeAttribute('hidden');
          }
        }}
        isDayHighlighted={(day1) => datesListHighlight.some((day2: DateHighlightProps) => isSameDay(day1, day2.date))}
        displayFormat={displayFormat}
        weekDayFormat="ddd"
      />
      {errorMessage && ((!isValid && !focusedInput && !isSingleDate) || forceError) && (
        <div className="input-error">
          <i className="material-icons notranslate">&#xe001;</i>
          <label id={idErrorMessage} htmlFor="input-text">
            {errorMessage}
          </label>
        </div>
      )}
    </div>
  );
};

SmlsCalendar.defaultProps = {
  isOpenDatePicker: false,
  showCloseButton: true,
  setIsOpenDatePicker: () => {},
  onPrevious: () => {},
  onToggle: () => {},
  onSkip: () => {},
  onClear: () => {},
  hasNavHeader: false,
};

export default SmlsCalendar;
